import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import pick from 'lodash/pick';
import * as supportRoles from '@purple/match-graphql-schema/lib/schema/support/roles'; // eslint-disable-line import/no-extraneous-dependencies
import Grid from '../../components/Grid';

import { translateError } from '../../cognito/cognito-service';
import { login, clearLoginErrors } from '../Authorization/actions';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Icon from '../../components/Icon';
import Container from '../../components/Container';
import Loader from '../../components/Loader';
import Dimmer from '../../components/Dimmer';
import Segment from '../../components/Segment';
import Form from '../../components/Form';
import Label from '../../components/Label';
import Message from '../../components/Message';
import Radio from '../../components/Radio';
import PageTitle from '../../components/PageTitle';

import './styles.css';

const missingValueLabel = (
  <Label role="tooltip" basic color="red" pointing data-cy="login-form-error-message">
    <FormattedMessage id="login-page.missingValue" defaultMessage="Please enter a value" />
  </Label>
);

const messages = defineMessages({
  loggingIn: {
    id: 'login-page.loggingIn',
    defaultMessage: 'Logging In...',
  },
  username: {
    id: 'login-page.placeholder.username',
    defaultMessage: 'E-Mail Address',
  },
  password: {
    id: 'login-page.placeholder.password',
    defaultMessage: 'Password',
  },
});

function validateForm(formData) {
  const newState = {
    missingName: null,
    missingPassword: null,
  };
  if (formData.username.length === 0) {
    newState.missingName = missingValueLabel;
  }
  if (formData.password.length === 0) {
    newState.missingPassword = missingValueLabel;
  }
  return newState;
}

class LoginPage extends Component {
  static propTypes = {
    current: PropTypes.object.isRequired,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    login: PropTypes.func.isRequired,
    clearLoginErrors: PropTypes.func.isRequired,
  };

  state = {
    username: '',
    password: '',
    requesting: false,
    errorCode: '',
    missingName: false,
    missingPw: false,
    impersonate: '',
  };

  componentDidMount() {
    this.props.clearLoginErrors();
  }

  componentWillReceiveProps({ current }) {
    const { errorCode } = current.viewer;
    if (errorCode !== this.state.errorCode) {
      this.setState({
        errorCode,
        requesting: false,
      });
    }
  }

  _onChange = (e, { value, name } = {}) => {
    value = value || e.target.value;
    name = name || e.target.name;
    this.setState({ [name]: value });
  };

  _onImpersonate = (e, { value } = {}) => {
    /* istanbul ignore next */
    this.setState({ impersonate: value });
  };

  _onSubmit = (e) => {
    const formData = pick(this.state, 'username', 'password', 'impersonate');
    e.preventDefault();
    const newState = validateForm(formData);
    if (!Object.values(newState).some((value) => value)) {
      this.props.login(formData);
      newState.requesting = true;
      newState.errorCode = null;
    }
    this.setState(newState);
  };

  render() {
    const { intl } = this.props;
    const { errorCode, missingName, missingPassword, requesting, username, password } = this.state;
    const errorDetails = errorCode ? (
      <Message role="alert" negative data-cy="login-form-error-message-tooltip">
        <Message.Header><FormattedMessage {...translateError(errorCode)} /></Message.Header>
      </Message>
    ) : '';
    return (
      <Container text className="loginPanel">
        <PageTitle title="Login" category="Intersect" />
        <Dimmer.Dimmable as={Form} onSubmit={this._onSubmit} size="large">
          <Dimmer
            role="progressbar"
            aria-describedby={intl.formatMessage(messages.loggingIn)}
            aria-busy={requesting}
            inverted
            active={requesting}
          >
            <Loader active={requesting} />
          </Dimmer>
          <Segment>
            <Form.Field>
              <label htmlFor="username">
                <FormattedMessage {...messages.username} />
              </label>
              <Input type="text" name="username" value={username} icon iconPosition="left" onChange={this._onChange}>
                <Icon name="mail" />
                <input
                  id="username"
                  autoComplete="off"
                  autoCapitalize="off"
                  aria-invalid={!!missingName}
                />
              </Input>
              {missingName}
            </Form.Field>
            <Form.Field>
              <label htmlFor="password">
                <FormattedMessage {...messages.password} />
              </label>
              <Input type="password" name="password" value={password} icon iconPosition="left" onChange={this._onChange}>
                <Icon name="lock" />
                <input id="password" autoComplete="off" aria-required aria-invalid={!!missingPassword} />
              </Input>
              {missingPassword}
            </Form.Field>
            <Button primary type="submit" data-cy="login-form-submit-button">
              <FormattedMessage id="login-page.loginButton" defaultMessage="Login" />
            </Button>
          </Segment>
        </Dimmer.Dimmable>
        {errorDetails}
        <Grid styleName="gridFontSize">
          <Grid.Column floated="left" width={8}>
            <Link href={process.env.REGISTRATION_URL} data-cy="login-form-new-user-link">
              <FormattedMessage id="he-registration.new-user" defaultMessage="New User?" />
            </Link>
          </Grid.Column>
          <Grid.Column floated="right" width={8} textAlign="right">
            <Link to="/forgot-password" data-cy="login-form-forgot-password-link">
              <FormattedMessage id="he-registration.forgotPassword" defaultMessage="Forgot Password" />
            </Link>
          </Grid.Column>
        </Grid>
        {process.env.__DEVCLIENT__ && (
        <div styleName="devImpersonateDiv">
          Impersonate as:&nbsp;&nbsp;
          <Radio
            styleName="devImpersonateLabel"
            data-cy="login-form-none-impersonate"
            name="supportRoles"
            aria-label="do not impersonate"
            value=""
            onChange={this._onImpersonate}
            checked={this.state.impersonate === ''}
            label="None (default)"
          />
          <Radio
            styleName="devImpersonateLabel"
            data-cy="login-form-support-impersonate"
            name="supportRoles"
            aria-label="support"
            value={supportRoles.SUPPORT_ROLE}
            onChange={this._onImpersonate}
            checked={this.state.impersonate === supportRoles.SUPPORT_ROLE}
            label="Support"
          />
          <Radio
            styleName="devImpersonateLabel"
            data-cy="login-form-administrator-impersonate"
            name="supportRoles"
            aria-label="administrator"
            value={supportRoles.ADMIN_ROLE}
            onChange={this._onImpersonate}
            checked={this.state.impersonate === supportRoles.ADMIN_ROLE}
            label="Administrator"
          />
          <Radio
            styleName="devImpersonateLabel"
            data-cy="login-form-superadmin-impersonate"
            name="supportRoles"
            aria-label="superadmin"
            value={supportRoles.SUPER_ADMIN_ROLE}
            onChange={this._onImpersonate}
            checked={this.state.impersonate === supportRoles.SUPER_ADMIN_ROLE}
            label="SuperAdmin"
          />
        </div>
        )}
      </Container>
    );
  }
}

const mapStateToProps = (state) => ({
  current: state.current,
});

export const LoginPageComponent = injectIntl(LoginPage);

export default connect(mapStateToProps, { login, clearLoginErrors })(LoginPageComponent);
